import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IceIcon, PageContainer, PageHeader, TextInput, UserMenu, DialogTitleWithClose } from '../components';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import UserContact, { UserContactSkeleton, validateUserContact } from './UserContact';
import { useJspVariables, useAuth, useLoader, useReferenceData, useDebugRender, usePageTitle } from '../hooks';
import { getLabelFromJspVariables } from '../common/JspUtil';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const PageProfile = () => {
    useDebugRender('PageProfile');
    usePageTitle('Profile');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { launcher, panelStyle } = useJspVariables();
    const { showLoader, hideLoader } = useLoader();

    const { authenticatedUser, isAuthenticated, ssoEnvironment, loading } = useAuth();
    const { countries, countryByCode } = useReferenceData();

    const [userProfile, setUserProfile] = useState(null);
    const [showOtpDialog, setShowOtpDialog] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (authenticatedUser && authenticatedUser.user) {
            if (!countries || countries.length === 0) {
                dispatch(actions.getReferenceData(authenticatedUser.user, authenticatedUser.app));
            }
            actions.loadUserProfile(authenticatedUser.user, authenticatedUser.app)
                .then(resp => setUserProfile(resp.data))
                .catch(err => {
                    console.error('loadUserProfile failed:', err);
                    setAlert({ message: err.message || getLabelFromJspVariables('err_contact_load_failed'), severity: 'error' });
                });
        }
    }, [authenticatedUser]);

    const getAreacodes = () => {
        let areacodes = [];
        areacodes.push({ countryCode: 'US', countryName: 'United States', phoneCode: '+1', displayCode: 'US +1', code: 'US+1' });
        if (countries && countries.length > 0) {
            for (let country of [...countries].sort((x, y) => x.countryCode < y.countryCode ? -1 : 1)) {
                if (country.countryCode != 'US') {
                    areacodes.push({ ...country, displayCode: country.countryCode + ' ' + country.phoneCode, code: country.countryCode + country.phoneCode });
                }
            }
        }
        return areacodes;
    };

    const validate = () => {
        setAlert(null);
        const fieldErrors = validateUserContact(userProfile, ssoEnvironment, getAreacodes(), countryByCode);
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const upgradeSession = async () => {
        setAlert(null);
        if (!otpCode.trim()) {
            setErrors(prev => ({ ...prev, otpCode: errorCodes.MSG_FIELD_REQUIRED() }));
            return;
        }
        setErrors({});
        try {
            await actions.upgradeSession(authenticatedUser.user, authenticatedUser.app, otpCode);
            setShowOtpDialog(false);
            setOtpCode('');
            showLoader('Loading user contact...');
            try {
                const resp = await actions.loadUserProfile(authenticatedUser.user, authenticatedUser.app);
                setUserProfile(resp.data);
            } finally {
                hideLoader();
            }
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        }
    };

    const saveUserProfile = async () => {
        if (!validate()) return;
        setAlert(null);
        try {
            const resp = await actions.saveUserProfile({
                ...userProfile,
                userName: authenticatedUser.user,
                userId: authenticatedUser.user,
                appKey: authenticatedUser.app
            });
            setUserProfile(resp.data);
            dispatch({ type: 'USER_CONTACT_VERIFIED', data: true });
            setAlert({ message: getLabelFromJspVariables('alert_contact_saved'), severity: 'success' });
            setTimeout(() => navigate(constants.ROUTE_PAGE_AUTHENTICATED), 1500);
        } catch (resp) {
            if (resp.code == errorCodes.ERR_REQUIRES_OTP_ENTERED_AUTH) {
                dispatch(actions.logoutUserToken(authenticatedUser.user, false, false));
            } else {
                setAlert({ message: resp.message || getLabelFromJspVariables('err_contact_save_failed'), severity: 'error' });
            }
        }
    };

    const verifyUser = async () => {
        setAlert(null);
        try {
            await actions.sendUpgradeCode(authenticatedUser.user, authenticatedUser.app);
            setShowOtpDialog(true);
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        }
    };

    const no2FA = !userProfile || !userProfile.tfaMethod || userProfile.tfaMethod === 'Disabled';
    const canSave = userProfile && userProfile.tfaMethod === 'TFA_OTP';
    const isReadOnly = no2FA || !canSave;

    if (loading) return null;
    if (!isAuthenticated) {
        navigate(constants.ROUTE_PAGE_LOGIN);
        return null;
    }

    return (
        <PageContainer id="pageProfile" dataAutomationId="page_profile" md={10} lg={8}>
            <Box style={panelStyle}>
                <MuiDialog open={showOtpDialog} onClose={() => setShowOtpDialog(false)} maxWidth="sm" fullWidth
                    aria-labelledby="profile-verify-identity-title"
                    aria-describedby="profile-verify-identity-description">
                    <DialogTitleWithClose severity="info" id="profile-verify-identity-title" onClose={() => setShowOtpDialog(false)} dataAutomationId="profile_verify_identity_title">{getLabelFromJspVariables('title_verify_identity')}</DialogTitleWithClose>
                    <DialogContent id="profile-verify-identity-description">
                        <Alert severity="info" aria-live="polite" sx={{ mb: 1 }}>{getLabelFromJspVariables('note_verify_identity_otp')}</Alert>
                        {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                        <TextInput
                            label={getLabelFromJspVariables('label_otp_code')}
                            name="verificationCode"
                            id="otp_code_input"
                            dataAutomationId="otp_code_input"
                            autoComplete="one-time-code"
                            required
                            numericOnly maxLength={6}
                            value={otpCode}
                            error={!!errors.otpCode}
                            helperText={errors.otpCode || ''}
                            onChange={e => { setOtpCode(e.target.value); if (errors.otpCode) setErrors(prev => ({ ...prev, otpCode: '' })); }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setShowOtpDialog(false)} data-automation-id="profile_otp_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                        <Button variant="contained" onClick={upgradeSession} data-automation-id="profile_otp_verify_btn">{getLabelFromJspVariables('action_verify')}</Button>
                    </DialogActions>
                </MuiDialog>
                <UserMenu
                    authenticatedUser={authenticatedUser}
                    onLogout={() => dispatch(actions.logoutUserToken(authenticatedUser.user))}
                    onNavigate={(route) => navigate(route)}
                />
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="profile_header">{getLabelFromJspVariables('title_user_contact')}</PageHeader>
                {alert && !showOtpDialog && <Alert id="pageProfileAlert" data-automation-id="page_profile_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                {userProfile ?
                    <UserContact
                        userProfile={userProfile}
                        onProfileChange={setUserProfile}
                        readOnly={isReadOnly}
                        authenticatedUser={authenticatedUser}
                        launcher={launcher}
                        countries={countries}
                        countryByCode={countryByCode}
                        errors={errors}
                        onClearError={clearFieldError}
                    />
                    :
                    <UserContactSkeleton />
                }
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    {no2FA ?
                        <Button variant="contained"
                            onClick={() => navigate(constants.ROUTE_PAGE_AUTHENTICATED)}
                            data-automation-id="profile_close_btn">
                            {getLabelFromJspVariables('action_close')}
                        </Button>
                        :
                        <>
                            <Button variant="outlined"
                                onClick={() => navigate(constants.ROUTE_PAGE_AUTHENTICATED)}
                                data-automation-id="profile_cancel_btn">
                                {getLabelFromJspVariables('action_cancel')}
                            </Button>
                            {canSave ?
                                <Button variant="contained" onClick={saveUserProfile}
                                    data-automation-id="profile_save_btn">
                                    {getLabelFromJspVariables('action_save')}
                                </Button>
                                :
                                <Button variant="contained" onClick={verifyUser}
                                    data-automation-id="profile_edit_contact_btn">
                                    {getLabelFromJspVariables('action_edit_contact')}
                                </Button>
                            }
                        </>
                    }
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageProfile;
